import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Modal, Select, Show } from "shared/components";
import {
  lockboxesApi,
  Lockbox,
  GeneratedLockboxCode,
  SENTRILOCK_CODE_DURATIONS,
  SentrilockCodeDuration
} from "api/landlord/lockboxes";
import { getApiErrorKey, getErrorMessage } from "utils/error";

const ERROR_MESSAGES: Record<string, string> = {
  lockbox_account_unassigned:
    "Assign a vendor account to this lockbox before generating a code. You can do that from the Lockboxes screen.",
  integration_not_connected:
    "This lockbox's vendor isn't connected. Add credentials under Settings → Integrations first.",
  invalid_duration: "Pick one of the offered durations and try again.",
  duplicate_request:
    "A code is already being generated for this lockbox. Give it a moment and try again.",
  sentrilock_no_code_returned:
    "SentriLock didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  codebox_no_code_returned:
    "Codebox didn't return a code for this serial number. Check the serial number and the assigned account, then try again."
};

const generateCodeErrorMessage = (error: unknown) => {
  const key = getApiErrorKey(error);
  return key && ERROR_MESSAGES[key]
    ? ERROR_MESSAGES[key]
    : getErrorMessage(error);
};

const DURATION_LABELS: Record<SentrilockCodeDuration, string> = {
  30: "30 minutes",
  60: "1 hour",
  120: "2 hours",
  240: "4 hours"
};

const DURATION_OPTIONS = SENTRILOCK_CODE_DURATIONS.map((value) => ({
  label: DURATION_LABELS[value],
  value
}));

export const GenerateLockboxCodeModal = ({
  opened,
  onClose,
  onGenerated,
  lockbox
}: {
  opened: boolean;
  onClose: () => void;
  onGenerated: (result: GeneratedLockboxCode) => void;
  lockbox: Lockbox;
}) => {
  const queryClient = useQueryClient();
  const isSentrilock = lockbox.vendor === "sentrilock";

  const [duration, setDuration] = useState<SentrilockCodeDuration>(
    SENTRILOCK_CODE_DURATIONS[0]
  );

  const { mutate: generate, isPending } = useMutation({
    mutationKey: ["LOCKBOX", "GENERATE_CODE", "CREATE", lockbox.id],
    mutationFn: () =>
      lockboxesApi.generateCode(
        lockbox.id,
        isSentrilock ? { duration_minutes: duration } : {}
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
      onGenerated(result);
      setDuration(SENTRILOCK_CODE_DURATIONS[0]);
    },
    onError: (err) => {
      toast.error(generateCodeErrorMessage(err));
    }
  });

  const handleClose = () => {
    if (isPending) return;
    setDuration(SENTRILOCK_CODE_DURATIONS[0]);
    onClose();
  };

  return (
    <Modal
      zIndex={120}
      opened={opened}
      onClose={handleClose}
      panelClassName="relative max-w-[366px] w-full border border-alpha-100 p-6 space-y-4 shadow-lg"
    >
      <div className="space-y-2">
        <p className="text-center text-xl font-bold text-content-1000">
          Generate lockbox code
        </p>
        <Show if={isSentrilock}>
          <p className="text-center text-base text-content-500">
            How long should the code work?
          </p>
        </Show>
      </div>

      <div className="space-y-4">
        <Show if={isSentrilock}>
          <div className="space-y-4">
            <Select
              id="sentrilock-duration"
              aria-label="Code duration"
              options={DURATION_OPTIONS}
              value={duration}
              searchable={false}
              clearable={false}
              onChange={(e) => {
                const parsed = Number(e.target.value);
                const isValidDuration = (
                  SENTRILOCK_CODE_DURATIONS as readonly number[]
                ).includes(parsed);
                if (isValidDuration)
                  setDuration(parsed as SentrilockCodeDuration);
              }}
            />
            <div className="rounded-lg bg-warning-50 p-4">
              <p className="text-sm font-medium text-warning-700">
                Starts as soon as you generate it
              </p>
            </div>
          </div>
        </Show>

        <Show if={!isSentrilock}>
          <div className="rounded-lg bg-warning-50 p-4">
            <p className="text-sm font-medium text-warning-700">
              Codebox codes work for the rest of today and can&apos;t be
              shortened or canceled once created.
            </p>
          </div>
        </Show>

        <div className="flex w-full gap-2">
          <Button className="w-full" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={() => generate()}
            loading={isPending}
            disabled={isPending}
          >
            Generate
          </Button>
        </div>
      </div>
    </Modal>
  );
};
